/**
 * Thin fetch wrapper. The API base URL is inferred from the current hostname:
 * `dashboard.appsforcheap.com` → `https://api.appsforcheap.com`, etc.
 *
 * `credentials: 'include'` is baked in so the session cookie (set on the
 * shared `.appsforcheap.com` / `.webforcheap.com` domain) rides along.
 */
export function inferApiUrl(): string {
  if (typeof window === 'undefined') return '';
  const host = window.location.hostname;
  if (host.endsWith('webforcheap.com')) return 'https://api.webforcheap.com';
  if (host.endsWith('appsforcheap.com')) return 'https://api.appsforcheap.com';
  // Local dev — assume dev API.
  return 'https://api.appsforcheap.com';
}

export const API_URL: string = inferApiUrl();

export async function api(path: string, opts: RequestInit = {}): Promise<Response> {
  return fetch(API_URL + path, { credentials: 'include', ...opts });
}

/** Convenience: parse JSON, throw with `error` field on non-2xx. */
export async function apiJson<T = unknown>(path: string, opts: RequestInit = {}): Promise<T> {
  const r = await api(path, opts);
  const body = await r.json().catch(() => ({}));
  if (!r.ok) {
    const msg = (body as { error?: string }).error || `Request failed (${r.status})`;
    throw new Error(msg);
  }
  return body as T;
}

export function escapeHtml(text: string | null | undefined): string {
  if (text == null) return '';
  const div = document.createElement('div');
  div.textContent = String(text);
  return div.innerHTML;
}

export function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const m = Math.floor(diffMs / 60_000);
  const h = Math.floor(diffMs / 3_600_000);
  const d = Math.floor(diffMs / 86_400_000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m} min ago`;
  if (h < 24) return `${h} hour${h > 1 ? 's' : ''} ago`;
  if (d < 7) return `${d} day${d > 1 ? 's' : ''} ago`;
  return date.toLocaleDateString();
}
